import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";
import { type LucideIcon } from "lucide-react";

interface Props {
  translationPrefix: string;
  icons: LucideIcon[];
}

const IndustryChallengesI18n = ({ translationPrefix, icons }: Props) => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject } = useLanguage();
  const items = tObject<Array<{ title: string; description: string }>>(`${translationPrefix}.challenges.items`);

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn("py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center"><Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">{t(`${translationPrefix}.challenges.badge`)}</Badge></div>
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6 text-center">{t(`${translationPrefix}.challenges.title`)}</h2>
          <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">{t(`${translationPrefix}.challenges.intro`)}</p>
          <div className="grid md:grid-cols-2 gap-6">
            {items.map((item, index) => {
              const Icon = icons[index] || icons[0];
              return (
                <div key={index} className={cn("bg-background rounded-2xl p-6 border border-border transition-all duration-500", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")} style={{ transitionDelay: isVisible ? `${index * 100}ms` : "0ms" }}>
                  <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-destructive" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustryChallengesI18n;
